import withDisplayName from 'bz-utilities/hoc/display-name';

import { applyWrappedComponent } from 'bz-utilities/hoc/wrapped-component';
import React from 'react';
import z from 'z';
import type { IOptions } from '../../utils/loggers';
import ErrorLogger from '../../utils/loggers';
import DefaultFallback from './fallback';

export type TErrorFallback = (
  location: string,
  error?: Error,
  errorInfo?: React.ErrorInfo,
  loggedErrors?: string[]
) => React.ReactNode;

export interface IProps extends React.PropsWithChildren {
  location: string;
  fallback?: TErrorFallback;
  options?: Partial<IOptions>;
}

export interface IState {
  hasError?: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  loggedErrors?: string[];
}

export const withErrorBoundary = <T extends U, U extends object>(
  Comp: React.ComponentType<U>,
  location: string,
  fallback?: TErrorFallback
) => {
  const WrappedComponent: React.FC<T> = (props: T) => {
    return (
      <BzErrorBoundary location={location} fallback={fallback}>
        <Comp {...props} />
      </BzErrorBoundary>
    );
  };

  return applyWrappedComponent(WrappedComponent, Comp, 'withErrorBoundary');
};

class BzErrorBoundary extends React.Component<IProps, IState> {
  state: Readonly<IState> = {};

  static readonly defaultProps: Partial<IProps> = {
    fallback: DefaultFallback,
    options: {}
  };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error: error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo,
      loggedErrors: ErrorLogger([error, errorInfo], this.options)
    });
  }

  render() {
    const { children, fallback, location } = this.props;
    const { error, errorInfo, loggedErrors } = this.state;

    let formatted = location;
    try {
      formatted = z.format(location);
    } catch {}
    if (!error) {
      return children;
    }

    return <>{fallback?.(formatted, error, errorInfo, loggedErrors)}</>;
  }

  private get options(): Partial<IOptions> {
    const { location, options } = this.props;

    return {
      ...options,
      location: location
    };
  }
}

export default BzErrorBoundary;
